import { useRef, useState } from "preact/hooks"
import { ChevronDown, ChevronUp, Loader2, Plus, Trash2 } from "lucide-react"
import { Promotion } from "../../../promo_creation/types"
import {
  createPromotionPerformanceMetric,
  deletePromotionPerformanceMetric,
  PromotionPerformanceMetricInput,
  PromotionPerformanceMetricRow,
  updatePromotionPerformanceMetric,
} from "../../../../../api/promotion_effectiveness"
import { Button } from "../../../../../components/ui/button"
import { Checkbox } from "../../../../../components/ui/checkbox"
import { Input } from "../../../../../components/ui/input"
import { Label } from "../../../../../components/ui/label"
import { toast } from "../../../../../components/ui/use-toast"
import {
  default_metric_input,
  editor_focus_label,
  focus_fields_for_editor,
  format_money_input,
  is_manual_metric_row,
  metric_row_to_input,
  parse_money_input,
  PerformanceEditorFocus,
} from "./promotion_performance_editor_utils"

interface PromotionPerformanceEditorProps {
  promotion: Promotion
  metric_rows: PromotionPerformanceMetricRow[]
  focus?: PerformanceEditorFocus
  default_show_grid?: boolean
  on_saved: () => void
}

interface MetricGridCellProps {
  row_id: string
  field: keyof PromotionPerformanceMetricInput
  value: string
  editable: boolean
  on_save: (row_id: string, field: keyof PromotionPerformanceMetricInput, value: string) => void
}

function MetricGridCell({ row_id, field, value, editable, on_save }: MetricGridCellProps) {
  const [editing, set_editing] = useState(false)
  const [local_value, set_local_value] = useState(value)
  const last_prop_value = useRef(value)

  if (value !== last_prop_value.current) {
    last_prop_value.current = value
    set_local_value(value)
  }

  if (!editable) {
    return <span class="text-gray-700">{value || "-"}</span>
  }

  function handle_save() {
    set_editing(false)
    if (local_value !== (last_prop_value.current ?? "")) {
      on_save(row_id, field, local_value)
    }
  }

  return editing ? (
    <Input
      value={local_value}
      onInput={(event: Event) => set_local_value((event.target as HTMLInputElement).value)}
      onBlur={handle_save}
      onKeyDown={(event: KeyboardEvent) => {
        if (event.key === "Enter") handle_save()
        if (event.key === "Escape") {
          set_local_value(last_prop_value.current)
          set_editing(false)
        }
      }}
      className="h-8 min-w-[7rem]"
      autoFocus
    />
  ) : (
    <button type="button" class="text-left text-gray-700 hover:text-blue-700" onClick={() => set_editing(true)}>
      {value || "-"}
    </button>
  )
}

export function PromotionPerformanceEditor({
  promotion,
  metric_rows,
  focus = "all",
  default_show_grid = false,
  on_saved,
}: PromotionPerformanceEditorProps) {
  const [form, set_form] = useState<PromotionPerformanceMetricInput>(() => default_metric_input(promotion))
  const [show_advanced, set_show_advanced] = useState(focus === "all")
  const [show_grid, set_show_grid] = useState(default_show_grid || metric_rows.some(is_manual_metric_row))
  const [saving, set_saving] = useState(false)

  const focus_fields = focus_fields_for_editor(focus)
  const manual_rows = metric_rows.filter(is_manual_metric_row)
  const read_only_rows = metric_rows.filter((row: PromotionPerformanceMetricRow) => !is_manual_metric_row(row))

  function update_form_field(field: keyof PromotionPerformanceMetricInput, value: PromotionPerformanceMetricInput[keyof PromotionPerformanceMetricInput]) {
    set_form((current: PromotionPerformanceMetricInput) => ({ ...current, [field]: value }))
  }

  function field_class(field: string): string {
    return focus_fields.includes(field) ? "ring-1 ring-blue-200 rounded-md" : ""
  }

  async function handle_create_row() {
    if (!promotion.id) return
    if (!form.period_start || !form.period_end) {
      toast({ title: "Period required", description: "Enter a period start and end date.", variant: "destructive" })
      return
    }
    set_saving(true)
    try {
      await createPromotionPerformanceMetric(promotion.id, form)
      set_form(default_metric_input(promotion))
      toast({ title: "Metrics saved", description: "Performance metrics were added for this promotion." })
      on_saved()
    } catch (error) {
      toast({
        title: "Save failed",
        description: error instanceof Error ? error.message : "Could not save performance metrics.",
        variant: "destructive",
      })
    } finally {
      set_saving(false)
    }
  }

  async function handle_grid_save(row_id: string, field: keyof PromotionPerformanceMetricInput, raw_value: string) {
    if (!promotion.id) return
    const row = manual_rows.find((item: PromotionPerformanceMetricRow) => item.id === row_id)
    if (!row) return

    const body: PromotionPerformanceMetricInput = {}
    if (field === "feature_support" || field === "display_support") {
      body[field] = raw_value === "true"
    } else if (
      field === "total_sales_dollars" ||
      field === "baseline_sales_dollars" ||
      field === "incremental_sales_dollars" ||
      field === "total_units" ||
      field === "baseline_units" ||
      field === "incremental_units" ||
      field === "incremental_margin_dollars" ||
      field === "promo_price" ||
      field === "regular_price" ||
      field === "discount_depth_pct"
    ) {
      body[field] = parse_money_input(raw_value)
    } else if (
      field === "period_start" ||
      field === "period_end" ||
      field === "customer" ||
      field === "retailer" ||
      field === "product" ||
      field === "notes"
    ) {
      body[field] = raw_value || null
    }

    try {
      await updatePromotionPerformanceMetric(promotion.id, row_id, body)
      on_saved()
    } catch (error) {
      toast({
        title: "Update failed",
        description: error instanceof Error ? error.message : "Could not update performance metric.",
        variant: "destructive",
      })
    }
  }

  async function handle_delete_row(row_id: string) {
    if (!promotion.id) return
    try {
      await deletePromotionPerformanceMetric(promotion.id, row_id)
      toast({ title: "Metric deleted", description: "The performance metric row was removed." })
      on_saved()
    } catch (error) {
      toast({
        title: "Delete failed",
        description: error instanceof Error ? error.message : "Could not delete performance metric.",
        variant: "destructive",
      })
    }
  }

  const grid_columns: Array<{ key: keyof PromotionPerformanceMetricInput; label: string; money?: boolean }> = [
    { key: "period_start", label: "Period start" },
    { key: "period_end", label: "Period end" },
    { key: "product", label: "Product" },
    { key: "baseline_sales_dollars", label: "Baseline $", money: true },
    { key: "incremental_sales_dollars", label: "Incremental $", money: true },
    { key: "total_sales_dollars", label: "Total $", money: true },
    { key: "incremental_units", label: "Incr units", money: true },
    { key: "incremental_margin_dollars", label: "Incr margin $", money: true },
    { key: "feature_support", label: "Feature" },
    { key: "display_support", label: "Display" },
    { key: "notes", label: "Notes" },
  ]

  function grid_cell_value(row: PromotionPerformanceMetricRow, key: keyof PromotionPerformanceMetricInput): string {
    const input = metric_row_to_input(row)
    const value = input[key]
    if (typeof value === "boolean") return value ? "true" : "false"
    if (typeof value === "number") return format_money_input(value)
    return value ?? ""
  }

  return (
    <div class="space-y-4 rounded-md border border-gray-200 p-4">
      <div class="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h4 class="text-sm font-semibold text-gray-900">{editor_focus_label(focus)}</h4>
          <p class="mt-1 text-sm text-gray-600">Enter performance metrics directly or expand the grid to edit all columns.</p>
        </div>
        <Button variant="outline" onClick={() => set_show_grid(!show_grid)}>
          {show_grid ? <ChevronUp className="mr-2 h-4 w-4" /> : <ChevronDown className="mr-2 h-4 w-4" />}
          {show_grid ? "Hide full grid" : "Show all columns"}
        </Button>
      </div>

      <div class="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        <FormField label="Period start" className={field_class("period_start")}>
          <Input type="date" value={form.period_start ?? ""} onInput={(event: Event) => update_form_field("period_start", (event.target as HTMLInputElement).value)} />
        </FormField>
        <FormField label="Period end" className={field_class("period_end")}>
          <Input type="date" value={form.period_end ?? ""} onInput={(event: Event) => update_form_field("period_end", (event.target as HTMLInputElement).value)} />
        </FormField>
        <FormField label="Baseline sales ($)" className={field_class("baseline_sales_dollars")}>
          <Input
            type="number"
            step="0.01"
            value={format_money_input(form.baseline_sales_dollars)}
            onInput={(event: Event) => update_form_field("baseline_sales_dollars", parse_money_input((event.target as HTMLInputElement).value))}
          />
        </FormField>
        <FormField label="Incremental sales ($)" className={field_class("incremental_sales_dollars")}>
          <Input
            type="number"
            step="0.01"
            value={format_money_input(form.incremental_sales_dollars)}
            onInput={(event: Event) => update_form_field("incremental_sales_dollars", parse_money_input((event.target as HTMLInputElement).value))}
          />
        </FormField>
        <FormField label="Total sales ($)" className={field_class("total_sales_dollars")}>
          <Input
            type="number"
            step="0.01"
            value={format_money_input(form.total_sales_dollars)}
            onInput={(event: Event) => update_form_field("total_sales_dollars", parse_money_input((event.target as HTMLInputElement).value))}
          />
        </FormField>
        <FormField label="Incremental units" className={field_class("incremental_units")}>
          <Input
            type="number"
            step="0.01"
            value={format_money_input(form.incremental_units)}
            onInput={(event: Event) => update_form_field("incremental_units", parse_money_input((event.target as HTMLInputElement).value))}
          />
        </FormField>
        <FormField label="Incremental margin ($)" className={field_class("incremental_margin_dollars")}>
          <Input
            type="number"
            step="0.01"
            value={format_money_input(form.incremental_margin_dollars)}
            onInput={(event: Event) => update_form_field("incremental_margin_dollars", parse_money_input((event.target as HTMLInputElement).value))}
          />
        </FormField>
        <div class="flex items-end gap-4 pb-2">
          <label class="flex items-center gap-2 text-sm text-gray-700">
            <Checkbox checked={Boolean(form.feature_support)} onCheckedChange={(checked: boolean) => update_form_field("feature_support", checked)} />
            Feature support
          </label>
          <label class="flex items-center gap-2 text-sm text-gray-700">
            <Checkbox checked={Boolean(form.display_support)} onCheckedChange={(checked: boolean) => update_form_field("display_support", checked)} />
            Display support
          </label>
        </div>
      </div>

      <div>
        <button type="button" class="text-sm text-blue-700 hover:text-blue-900" onClick={() => set_show_advanced(!show_advanced)}>
          {show_advanced ? "Hide advanced fields" : "Show advanced fields"}
        </button>
        {show_advanced ? (
          <div class="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            <FormField label="Customer">
              <Input value={form.customer ?? ""} onInput={(event: Event) => update_form_field("customer", (event.target as HTMLInputElement).value)} />
            </FormField>
            <FormField label="Retailer">
              <Input value={form.retailer ?? ""} onInput={(event: Event) => update_form_field("retailer", (event.target as HTMLInputElement).value)} />
            </FormField>
            <FormField label="Product">
              <Input value={form.product ?? ""} onInput={(event: Event) => update_form_field("product", (event.target as HTMLInputElement).value)} />
            </FormField>
            <FormField label="Total units">
              <Input type="number" step="0.01" value={format_money_input(form.total_units)} onInput={(event: Event) => update_form_field("total_units", parse_money_input((event.target as HTMLInputElement).value))} />
            </FormField>
            <FormField label="Baseline units">
              <Input type="number" step="0.01" value={format_money_input(form.baseline_units)} onInput={(event: Event) => update_form_field("baseline_units", parse_money_input((event.target as HTMLInputElement).value))} />
            </FormField>
            <FormField label="Promo price">
              <Input type="number" step="0.01" value={format_money_input(form.promo_price)} onInput={(event: Event) => update_form_field("promo_price", parse_money_input((event.target as HTMLInputElement).value))} />
            </FormField>
            <FormField label="Regular price">
              <Input type="number" step="0.01" value={format_money_input(form.regular_price)} onInput={(event: Event) => update_form_field("regular_price", parse_money_input((event.target as HTMLInputElement).value))} />
            </FormField>
            <FormField label="Discount depth %">
              <Input type="number" step="0.01" value={format_money_input(form.discount_depth_pct)} onInput={(event: Event) => update_form_field("discount_depth_pct", parse_money_input((event.target as HTMLInputElement).value))} />
            </FormField>
            <FormField label="Notes" className="md:col-span-2 xl:col-span-4">
              <Input value={form.notes ?? ""} onInput={(event: Event) => update_form_field("notes", (event.target as HTMLInputElement).value)} />
            </FormField>
          </div>
        ) : null}
      </div>

      <div class="flex justify-end">
        <Button onClick={handle_create_row} disabled={saving}>
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
          Add metrics row
        </Button>
      </div>

      {show_grid ? (
        <div class="space-y-3">
          <div class="max-h-72 overflow-auto rounded-md border border-gray-200">
            <table class="w-full text-sm">
              <thead class="sticky top-0 bg-gray-50 text-left text-xs uppercase text-gray-500">
                <tr>
                  {grid_columns.map((column) => (
                    <th key={column.key} class="px-3 py-2">{column.label}</th>
                  ))}
                  <th class="px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {manual_rows.length === 0 ? (
                  <tr>
                    <td colSpan={grid_columns.length + 1} class="px-3 py-4 text-gray-500">No manual metric rows yet.</td>
                  </tr>
                ) : (
                  manual_rows.map((row: PromotionPerformanceMetricRow) => (
                    <tr key={row.id} class="border-t border-gray-100">
                      {grid_columns.map((column) => (
                        <td key={column.key} class="px-3 py-2">
                          <MetricGridCell
                            row_id={row.id}
                            field={column.key}
                            value={grid_cell_value(row, column.key)}
                            editable
                            on_save={handle_grid_save}
                          />
                        </td>
                      ))}
                      <td class="px-3 py-2">
                        <Button variant="ghost" onClick={() => handle_delete_row(row.id)}>
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {read_only_rows.length > 0 ? (
            <div class="rounded-md border border-dashed border-gray-300 p-3 text-sm text-gray-600">
              {read_only_rows.length} syndicated or imported row{read_only_rows.length === 1 ? "" : "s"} are read-only here. Manual rows take priority in insights calculations.
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

interface FormFieldProps {
  label: string
  className?: string
  children: preact.ComponentChildren
}

function FormField({ label, className, children }: FormFieldProps) {
  return (
    <div class={className}>
      <Label className="mb-1 block text-xs font-medium uppercase text-gray-500">{label}</Label>
      {children}
    </div>
  )
}
